import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { UNITS } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import { Plus, PencilSimple, Trash, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";

const EMPTY = { name: "", description: "", hsn: "", unit: "NOS", rate: 0, gst_rate: 18, opening_stock: 0, min_stock: 0 };

export default function ProductsPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [{ data }, stockResp] = await Promise.all([
      api.get("/products", { params: { q: q || undefined } }),
      api.get("/inventory/stock").catch(() => ({ data: [] })),
    ]);
    const stockMap = {};
    for (const s of stockResp.data || []) stockMap[s.id] = s.current_stock;
    setRows(data.map(r => ({ ...r, current_stock: stockMap[r.id] ?? r.opening_stock ?? 0 })));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openCreate = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r.id); };
  const close = () => setEditing(null);

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form, rate: Number(form.rate || 0), gst_rate: Number(form.gst_rate || 0), opening_stock: Number(form.opening_stock || 0), min_stock: Number(form.min_stock || 0) };
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      toast.success("Product saved");
      close();
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="products-page">
      <PageHeader
        title="Products"
        subtitle="Goods & services with HSN/SAC and GST rates."
        actions={
          <>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input data-testid="search-products" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} placeholder="Search name, HSN, description…" className="pl-8 pr-3 py-2 text-sm bg-white border border-input rounded-md focus:ring-2 focus:ring-brand-secondary outline-none w-56" />
            </div>
            <button onClick={openCreate} className="btn-primary flex items-center gap-1.5" data-testid="add-product-button"><Plus size={14} weight="bold" /> New Product</button>
          </>
        }
      />

      <div className="p-8">
        <div className="table-wrapper">
          <table className="table-base">
            <thead><tr>
              <th>Name</th><th>HSN</th><th>Unit</th><th className="text-right">Rate</th><th className="text-right">GST %</th><th className="text-right">Opening</th><th className="text-right">Current Stock</th><th className="text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-ink-muted">No products yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} data-testid={`product-row-${r.id}`}>
                  <td className="font-semibold">{r.name}<div className="text-xs text-ink-muted font-normal">{r.description}</div></td>
                  <td className="font-mono text-xs">{r.hsn || "—"}</td>
                  <td>{r.unit}</td>
                  <td className="text-right">₹ {Number(r.rate).toFixed(2)}</td>
                  <td className="text-right">{r.gst_rate}%</td>
                  <td className="text-right tabular">{Number(r.opening_stock || 0).toFixed(2)}</td>
                  <td className={`text-right tabular font-semibold ${r.current_stock <= 0 ? "text-destructive" : "text-[#1C4F32]"}`}>{Number(r.current_stock || 0).toFixed(2)}</td>
                  <td className="text-right">
                    <button onClick={() => openEdit(r)} className="btn-ghost inline-flex items-center gap-1" data-testid={`edit-product-${r.id}`}><PencilSimple size={14} /> Edit</button>
                    <button onClick={() => remove(r.id)} className="btn-ghost inline-flex items-center gap-1 text-destructive" data-testid={`delete-product-${r.id}`}><Trash size={14} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-lg border border-border max-w-xl w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="font-display text-lg font-semibold">{editing === "new" ? "New Product" : "Edit Product"}</div>
              <button onClick={close} className="btn-ghost"><X size={16} /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <div className="label-overline block mb-1.5">Name *</div>
                <input className="field" data-testid="product-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <div className="label-overline block mb-1.5">Description</div>
                <input className="field" data-testid="product-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <div className="label-overline block mb-1.5">HSN / SAC</div>
                <input
                  className="field font-mono"
                  data-testid="product-hsn"
                  value={form.hsn}
                  onChange={e => setForm({ ...form, hsn: e.target.value })}
                  onBlur={async () => {
                    const code = (form.hsn || "").trim();
                    if (!code || code.length < 2) return;
                    try {
                      const { data } = await api.get(`/lookup/hsn/${code}`);
                      if (typeof data.gst_rate === "number") {
                        setForm(f => ({ ...f, gst_rate: data.gst_rate }));
                        toast.success(`GST ${data.gst_rate}% — ${data.description}`, { duration: 2200 });
                      }
                    } catch (err) { console.warn('HSN lookup failed; pick GST rate manually', err); }
                  }}
                  placeholder="e.g. 84322100"
                />
              </div>
              <div>
                <div className="label-overline block mb-1.5">Unit</div>
                <select className="field" data-testid="product-unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                  {UNITS.map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <div className="label-overline block mb-1.5">Rate (₹)</div>
                <input type="number" step="0.01" className="field" data-testid="product-rate" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} />
              </div>
              <div>
                <div className="label-overline block mb-1.5">GST %</div>
                <select className="field" data-testid="product-gst" value={form.gst_rate} onChange={e => setForm({ ...form, gst_rate: Number(e.target.value) })}>
                  {[0, 0.25, 3, 5, 12, 18, 28].map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
              <div>
                <div className="label-overline block mb-1.5">Opening Stock</div>
                <input type="number" step="0.01" className="field" data-testid="product-stock" value={form.opening_stock} onChange={e => setForm({ ...form, opening_stock: e.target.value })} />
              </div>
              <div>
                <div className="label-overline block mb-1.5">Min Stock Level (low-stock alert)</div>
                <input type="number" step="0.01" className="field" data-testid="product-min-stock" value={form.min_stock ?? 0} onChange={e => setForm({ ...form, min_stock: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface-muted">
              <button onClick={close} className="btn-secondary">Cancel</button>
              <button onClick={save} disabled={loading || !form.name} className="btn-primary" data-testid="save-product">{loading ? "Saving…" : "Save Product"}</button>
            </div>
          </div>
        </div>
      )}

      <FieldStyle />
    </div>
  );
}
